import { Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import Layout from './ui/components/layout/Layout';
import Home from './pages/home/Home';
import SignUp from './pages/signUp/SignUp';
import Login from './pages/login/Login';
import ProtectedRoute from './ProtectedRoute';
import UserDashboardLayout from './ui/components/layout/UserDashboardLayout';
import VerificationSent from './pages/cartItems/cartItemsContent/VerificationSent';

import {
  RealEstateHome,
  SingleProductPage,
  ConsumerGoodsHome,
  HomeLivingHome,
  ElectronicsHome,
  SalesHome,
  FoodAndDrinkHome,
  CartItems,
  CheckoutItems,
  CheckoutPaymentSuccess,
  UserDashboard,
  ShoppingOverview,
  DirectDebit,
  Favorites,
  RecentlyViewed,
  CheckoutPaymentFailed,
  // StaySmallSmallSinglePropertyPage,
  // Notifications,
  // CreditWallet,
} from './lazyImports';

import CartItemsSkeleton from './ui/components/skeletons/CartItemsSkeleton';
import CheckoutItemsSkeleton from './ui/components/skeletons/CheckoutItemsSkeleton';
import ProductDetailsSkeleton from './ui/components/skeletons/ProductDetailsSkeleton';
import CheckoutPaymentSkeleton from './ui/components/skeletons/CheckoutPaymentSkeleton';
import DashboardSummarySkeleton from './ui/components/skeletons/DashboardSummarySkeleton';
import LandingPageSkeleton from './ui/components/skeletons/LandingPageSkeleton';
import DynamicSubCategoryPage from './pages/DynamicSubcategoryPage';
import Summary from './pages/userDashboard/shopping/shoppingOverviewContents/Summary';
import Purchases from './pages/userDashboard/shopping/shoppingOverviewContents/purchase/Purchases';
import ForgotPassword from './pages/forgotPassword/ForgotPassword';
import ResetPassword from './pages/resetPassword/ResetPassword';
import ProfileSummary from './pages/userDashboard/accountProfile/contents/profileSummary/ProfileSummary';
import AccountVerification from './pages/userDashboard/accountProfile/contents/accountVerification/AccountVerification';
import DeliveryAddress from './pages/userDashboard/accountProfile/contents/deliveryAddress/DeliveryAddress';
import Feedback from './pages/userDashboard/accountProfile/contents/feedback/Feedback';
import ResolutionCentre from './pages/userDashboard/accountProfile/contents/resolutionCentre/ResolutionCentre';
import UserAccountProfile from './pages/userDashboard/accountProfile/UserAccountProfile';
import TermsOfUse from './pages/termsOfUse/TermsOfUse';
import HowItWorks from './pages/howItWorks/HowItWorks';
import BuyerAndVendorProtection from './pages/buyerAndVendor/BuyerAndVendorProtection';
import MarketplaceGuidelines from './pages/marketplaceGuidelines/MarketplaceGuidelines';
import RefundsAndReturns from './pages/refundsAndReturns/RefundsAndReturns';
import BuyingGuide from './pages/buyingGuide/BuyingGuide';
import DirectDebitSetUp from './pages/checkoutItems/directDebitSetup/directDebitBankSetUp/DirectDebitSetUp';
import DirectDebitInitialized from './pages/checkoutItems/directDebitSetup/directDebitBankSetUp/directDebitSetupForm/directDebitInitialized/DirectDebitInitialized';
import SearchProductResult from './pages/searchProductResult/SearchProductResult';
import PageNotFound from './pages/pageNotFound/PageNotFound';
import AboutUs from './pages/aboutUs/AboutUs';
import { StaySmallSmallSummary } from './pages/userDashboard/staySmallSmallDashboard/staySmallSmallSummary/StaySmallSmallSummary';
import { StaySmallSmallStay } from './pages/userDashboard/staySmallSmallDashboard/staySmallSmallStay/StaySmallSmallStay';
import { StaySmallSmallFavorites } from './pages/userDashboard/staySmallSmallDashboard/staySmallSmallFavorites/StaySmallSmallFavorites';
import { StaySmallSmallRecentlyViewed } from './pages/userDashboard/staySmallSmallDashboard/staySmallSmallRecentlyViewed/StaySmallSmallRecentlyViewed';
import { VerifyEmail } from './pages/verifyEmail/VerifyEmail';
import CompleteBooking from './pages/home/realEstateHome/realEstateContentSection/staySmallSmall/completeBooking/CompleteBooking';
import { StaySmallSmallSinglePropertyPage } from './pages/home/realEstateHome/realEstateContentSection/staySmallSmall/staySmallSmallSinglePropertyPage/StaySmallSmallSinglePropertyPage';
import StaySmallSmallProfile from './pages/userDashboard/staySmallSmallDashboard/StaySmallSmallProfile';
// import CreditWallet from './pages/userDashboard/shopping/shoppingOverviewContents/creditWallet/CreditWallet';

const AppRoutes = () => {
  return (
    <Routes>
      <Route path="" element={<Layout />}>
        <Route path="/" element={<Home />} />
        <Route
          path="home-living"
          element={
            <Suspense fallback={<LandingPageSkeleton />}>
              <HomeLivingHome />
            </Suspense>
          }
        />

        <Route
          path="electronics"
          element={
            <Suspense fallback={<LandingPageSkeleton />}>
              <ElectronicsHome />
            </Suspense>
          }
        />
        <Route
          path="sales-offers"
          element={
            <Suspense fallback={<LandingPageSkeleton />}>
              <SalesHome />
            </Suspense>
          }
        />

        <Route
          path="search-results"
          element={
            <Suspense fallback={<LandingPageSkeleton />}>
              <SearchProductResult />
            </Suspense>
          }
        />

        <Route
          path="real-estate"
          element={
            <Suspense fallback={<LandingPageSkeleton />}>
              <RealEstateHome />
            </Suspense>
          }
        />

        <Route
          path=":category/:sub_category"
          element={
            <Suspense fallback={<LandingPageSkeleton />}>
              <DynamicSubCategoryPage />
            </Suspense>
          }
        />

        <Route
          path="food-drink"
          element={
            <Suspense fallback={<LandingPageSkeleton />}>
              <FoodAndDrinkHome />
            </Suspense>
          }
        />

        <Route
          path="lifestyle-consumer-goods"
          element={
            <Suspense fallback={<LandingPageSkeleton />}>
              <ConsumerGoodsHome />
            </Suspense>
          }
        />

        <Route
          path=":category/:sub_category/:productID/:slug"
          element={
            <Suspense
              fallback={
                <ProductDetailsSkeleton showAside showRecommendations />
              }
            >
              <SingleProductPage />
            </Suspense>
          }
        />

        <Route
          path=":real-estate/shortlet/:id/:name"
          element={
            // <Suspense fallback={<LandingPageSkeleton />}>
            <StaySmallSmallSinglePropertyPage />
            // </Suspense>
          }
        />

        <Route
          path="terms-of-use"
          element={
            <Suspense fallback={<LandingPageSkeleton />}>
              <TermsOfUse />
            </Suspense>
          }
        />

        <Route
          path="how-it-works"
          element={
            <Suspense fallback={<LandingPageSkeleton />}>
              <HowItWorks />
            </Suspense>
          }
        />

        <Route
          path="about-us"
          element={
            <Suspense fallback={<LandingPageSkeleton />}>
              <AboutUs />
            </Suspense>
          }
        />

        <Route
          path="buyer-vendor-protection"
          element={
            <Suspense fallback={<LandingPageSkeleton />}>
              <BuyerAndVendorProtection />
            </Suspense>
          }
        />

        <Route
          path="marketplace-guidelines"
          element={
            <Suspense fallback={<LandingPageSkeleton />}>
              <MarketplaceGuidelines />
            </Suspense>
          }
        />

        <Route
          path="refunds-returns"
          element={
            <Suspense fallback={<LandingPageSkeleton />}>
              <RefundsAndReturns />
            </Suspense>
          }
        />

        <Route
          path="buying-guide"
          element={
            <Suspense fallback={<LandingPageSkeleton />}>
              <BuyingGuide />
            </Suspense>
          }
        />

        <Route path=":category/*" element={<PageNotFound />} />
        <Route path="*" element={<PageNotFound />} />
      </Route>

      <Route
        path="cart-items"
        element={
          <Suspense fallback={<CartItemsSkeleton />}>
            <CartItems />
          </Suspense>
        }
      />

      <Route element={<ProtectedRoute />}>
        <Route
          path="cart-items/checkout"
          element={
            <Suspense fallback={<CheckoutItemsSkeleton />}>
              <CheckoutItems />
            </Suspense>
          }
        />
        <Route
          path="cart-items/checkout/mandate/create/:downPayment?"
          element={<DirectDebitSetUp />}
        />
        <Route
          path="cart-items/checkout/direct-debit/success"
          element={<DirectDebitInitialized />}
        />
        <Route
          path="verification-document-sent"
          element={<VerificationSent />}
        />

        <Route
          path="cart-items/checkout/payment-success/:orderId"
          element={
            <Suspense fallback={<CheckoutPaymentSkeleton />}>
              <CheckoutPaymentSuccess />
            </Suspense>
          }
        />
        <Route
          path="cart-items/checkout/payment-failed/:orderId"
          element={
            <Suspense fallback={<CheckoutPaymentSkeleton />}>
              <CheckoutPaymentFailed />
            </Suspense>
          }
        />
      </Route>

      <Route path="/sign-up" element={<SignUp />} />
      <Route path="/sign-up/verify-email/:email" element={<VerifyEmail />} />

      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password/:email" element={<ResetPassword />} />

      <Route path="*" element={<PageNotFound />} />

      <Route element={<ProtectedRoute />}>
        <Route element={<UserDashboardLayout />}>
          <Route
            path="user-dashboard"
            element={
              <Suspense fallback={<DashboardSummarySkeleton />}>
                <UserDashboard />
              </Suspense>
            }
          >
            <Route
              path="shopping-overview"
              element={
                <Suspense fallback={<DashboardSummarySkeleton />}>
                  <ShoppingOverview />
                </Suspense>
              }
            >
              <Route index element={<Navigate to="summary" replace />} />
              <Route path="summary" element={<Summary />} />
              <Route path="purchases" element={<Purchases />} />
              {/* <Route
                path="credit-wallet"
                element={
                  <Suspense fallback={<DashboardSummarySkeleton />}>
                    <CreditWallet />
                  </Suspense>
                }
              /> */}
              <Route
                path="favorites"
                element={
                  <Suspense fallback={<DashboardSummarySkeleton />}>
                    <Favorites />
                  </Suspense>
                }
              />
              <Route
                path="direct-debit"
                element={
                  <Suspense fallback={<DashboardSummarySkeleton />}>
                    <DirectDebit />
                  </Suspense>
                }
              />
              <Route
                path="recently-viewed"
                element={
                  <Suspense fallback={<DashboardSummarySkeleton />}>
                    <RecentlyViewed />
                  </Suspense>
                }
              />
              <Route path="*" element={<PageNotFound />} />
            </Route>
            {/* <Route
              path="notifications"
              element={
                <Suspense fallback={<DashboardSummarySkeleton />}>
                  <Notifications />
                </Suspense>
              }
            /> */}

            {/* //Shortlet Profile Dashboard */}

            <Route
              path="/user-dashboard/staySmallSmall"
              element={
                <Suspense fallback={<DashboardSummarySkeleton />}>
                  <StaySmallSmallProfile />
                </Suspense>
              }
            >
              <Route index element={<Navigate to="summary" replace />} />
              <Route path="summary" element={<StaySmallSmallSummary />} />
              <Route path="stay" element={<StaySmallSmallStay />} />
              <Route path="favorites" element={<StaySmallSmallFavorites />} />
              <Route
                path="recently-viewed"
                element={<StaySmallSmallRecentlyViewed />}
              />
            </Route>
            {/* //Account Profile Dashboard */}
            <Route
              path="account-profile"
              element={
                <Suspense fallback={<DashboardSummarySkeleton />}>
                  <UserAccountProfile />
                </Suspense>
              }
            >
              <Route
                index
                element={<Navigate to="profile-summary" replace />}
              />
              <Route path="profile-summary" element={<ProfileSummary />} />
              <Route
                path="account-verification"
                element={<AccountVerification />}
              />
              <Route
                path="delivery-address"
                element={
                  <Suspense fallback={<DashboardSummarySkeleton />}>
                    <DeliveryAddress />
                  </Suspense>
                }
              />
              <Route
                path="feedback"
                element={
                  <Suspense fallback={<DashboardSummarySkeleton />}>
                    <Feedback />
                  </Suspense>
                }
              />
              <Route
                path="resolution-centre"
                element={
                  <Suspense fallback={<DashboardSummarySkeleton />}>
                    <ResolutionCentre />
                  </Suspense>
                }
              />
              <Route
                path="recently-viewed"
                element={
                  <Suspense fallback={<DashboardSummarySkeleton />}>
                    <RecentlyViewed />
                  </Suspense>
                }
              />
              <Route path="*" element={<PageNotFound />} />
            </Route>
            <Route
              path=":id/slug"
              element={
                <Suspense fallback={<ProductDetailsSkeleton />}>
                  <SingleProductPage />
                </Suspense>
              }
            />

            <Route path="*" element={<PageNotFound />} />
          </Route>
          <Route
            path="complete-booking"
            element={
              <Suspense fallback={<LandingPageSkeleton />}>
                <CompleteBooking />
              </Suspense>
            }
          />
        </Route>
      </Route>
    </Routes>
  );
};

export default AppRoutes;
